import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import Icon from '../docs/Icon'
import { usePrefs } from '@/state/prefs'
import Cased from '../ui/Cased'

export type EditOption<T extends string> = {
  value: T
  label: string
  /** A small preview rendered inside the option button. */
  preview?: ReactNode
}

type EditableProps<T extends string> = {
  /** What is being edited, e.g. "Headline font". Names the pin button. */
  label: string
  value: T
  options: Array<EditOption<T>>
  onChange: (value: T) => void
  /** The token or setting this choice writes, shown as a spec line. */
  token?: string
  /** Extra detail for the current choice: license, contrast, source. */
  detail?: ReactNode
  children: ReactNode
  /** Where the pin sits on the element. */
  pin?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'right' | 'inside-bottom-left' | 'inside-right' | 'inside-top-right' | 'below' | 'below-left' | 'below-center'
  className?: string
  as?: 'div' | 'span'
  /** Show the pin; set false to render the element plainly. */
  enabled?: boolean
  /** Further option groups shown in the same panel, each with its own heading. */
  more?: Array<{ label: string; value: string; options: Array<EditOption<string>>; onChange: (value: string) => void }>
  /** Heading for the main options when `more` groups follow. */
  optionsLabel?: string
  /** Custom controls shown after the option groups, such as color inputs. */
  extra?: ReactNode
  /** Attributes for the wrapping element, such as a scoped data-theme. */
  attrs?: Record<string, string>
}

/** An On/Off choice, main or in a group, shows as a switch rather than two buttons. */
const isSwitch = (options: Array<EditOption<string>>) =>
  options.length === 2 && options.every((option) => option.value === 'on' || option.value === 'off')

/**
 * A design note on a live element: a pin that opens a small panel of options,
 * the way a design tool annotates a frame. Every option is a real token or
 * setting, so the page is the specimen.
 */
export default function Editable<T extends string>({
  label,
  value,
  options,
  onChange,
  token,
  detail,
  children,
  pin = 'top-right',
  className = '',
  as: Tag = 'div',
  enabled = true,
  more = [],
  optionsLabel,
  extra,
  attrs,
}: EditableProps<T>) {
  const { prefs } = usePrefs()
  const [open, setOpen] = useState(false)
  const panelId = useId()
  const root = useRef<HTMLElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const anchor = useRef<HTMLSpanElement>(null)
  const panel = useRef<HTMLSpanElement>(null)

  // An open panel shifts sideways to stay inside the window, whichever way
  // its pin aligns it.
  useLayoutEffect(() => {
    const el = panel.current
    if (!open || !el) return
    el.style.translate = ''
    // Measured at rest: an opening animation's transform would replace the
    // centering one for that frame and throw the measurement off.
    el.style.animation = 'none'
    const rect = el.getBoundingClientRect()
    el.style.animation = ''
    const margin = 8
    const over = rect.right - (document.documentElement.clientWidth - margin)
    const under = margin - rect.left
    if (over > 0) el.style.translate = `${-Math.min(over, Math.max(0, rect.left - margin))}px 0`
    else if (under > 0) el.style.translate = `${under}px 0`
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        button.current?.focus()
      }
    }
    // Any click outside the pin and its panel closes it, including clicks
    // elsewhere inside a large region such as the hero.
    const onPointer = (event: PointerEvent) => {
      if (!anchor.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  const current = options.find((option) => option.value === value)
  const notes = prefs.notes && enabled

  return (
    <Tag
      {...attrs}
      ref={root as never}
      className={`mds-editable mds-editable--${pin}${open ? ' is-open' : ''}${notes ? ' has-notes' : ''} ${className}`}
    >
      {children}
      {notes && (
        <span ref={anchor} className="mds-editable__anchor">
          <button
            ref={button}
            type="button"
            className="mds-editable__pin"
            aria-label={`Edit ${label.toLowerCase()}`}
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen((state) => !state)}
          >
            <Icon name="pen" size={13} />
            <span className="mds-editable__pin-label">
              <Cased>{label}</Cased>
            </span>
          </button>
          <span ref={panel} id={panelId} role="group" aria-label={label} className="mds-editable__panel" hidden={!open}>
            <span className="mds-editable__title">{optionsLabel && !isSwitch(options) ? `${label}: ${optionsLabel}` : label}</span>
            {isSwitch(options) ? (
              <button
                type="button"
                role="switch"
                aria-checked={value === 'on'}
                className="mds-editable__switch"
                onClick={() => onChange((value === 'on' ? 'off' : 'on') as T)}
              >
                <span className="mds-switch-track" aria-hidden="true" />
                <Cased>{optionsLabel ?? label}</Cased>
              </button>
            ) : (
              <span className="mds-editable__options">
                {options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="mds-editable__option"
                    aria-pressed={option.value === value}
                    onClick={() => onChange(option.value)}
                  >
                    {option.preview && <span className="mds-editable__preview">{option.preview}</span>}
                    <span>{option.label}</span>
                  </button>
                ))}
              </span>
            )}
            {more.map((group) =>
              isSwitch(group.options) ? (
                <button
                  key={group.label}
                  type="button"
                  role="switch"
                  aria-checked={group.value === 'on'}
                  className="mds-editable__switch"
                  onClick={() => group.onChange(group.value === 'on' ? 'off' : 'on')}
                >
                  <span className="mds-switch-track" aria-hidden="true" />
                  <Cased>{group.label}</Cased>
                </button>
              ) : (
                <span key={group.label} className="mds-editable__group" role="group" aria-label={group.label}>
                  <span className="mds-editable__title">{group.label}</span>
                  <span className="mds-editable__options">
                    {group.options.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className="mds-editable__option"
                        aria-pressed={option.value === group.value}
                        onClick={() => group.onChange(option.value)}
                      >
                        {option.preview && <span className="mds-editable__preview">{option.preview}</span>}
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </span>
                </span>
              ),
            )}
            {extra}
            {(token || detail) && (
              <span className="mds-editable__spec">
                {token && (
                  <code>
                    {token}: {current?.label ?? value}
                  </code>
                )}
                {detail && <span>{detail}</span>}
              </span>
            )}
          </span>
        </span>
      )}
    </Tag>
  )
}
